import { useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Truck, Lock, Users, Target, Gauge, ArrowLeft, Flame, Download, Search, PhoneCall, Crown, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KPIS = [
  { key: "total_leads", label: "Total leads", icon: Users },
  { key: "qualified", label: "Qualified", icon: Target },
  { key: "hot_leads", label: "Hot leads", icon: Flame },
  { key: "booked_calls", label: "Booked calls", icon: PhoneCall },
];

const SOURCE_LABELS = {
  diagnostic: "Diagnostic",
  playbook: "Playbook",
  strategy_call: "Strategy CTA",
  strategy_playbook: "Playbook page",
  strategy_playbook_complete: "12-Step done",
  strategy_diagnostic_qualified: "Diagnostic ✓",
  strategy_diagnostic_developing: "Diagnostic (dev)",
  strategy_cpm: "CPM Calculator",
  strategy_export: "Export Hub",
};
const sourceLabel = (s) => SOURCE_LABELS[s] || s || "—";

const STATUS_STYLES = {
  new: "bg-slate-100 text-slate-700",
  contacted: "bg-blue-100 text-blue-700",
  won: "bg-emerald-100 text-emerald-700",
  lost: "bg-red-100 text-red-600",
};

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [founding, setFounding] = useState(null);
  const [showPass, setShowPass] = useState(false);

  // filters
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [hotOnly, setHotOnly] = useState(false);
  const [sort, setSort] = useState("newest");

  const headers = { "x-admin-passcode": passcode.trim() };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const [l, s, c] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/partner-clicks`, { headers }),
      ]);
      setLeads(l.data);
      setStats(s.data);
      setClicks(c.data);
      setAuthed(true);
      // Non-blocking: a public-status hiccup must not fail admin login.
      axios.get(`${API}/founding/status`).then((f) => setFounding(f.data)).catch(() => {});
    } catch {
      toast.error("Invalid passcode");
    } finally {
      setLoading(false);
    }
  };

  const resetFounding = async () => {
    if (!window.confirm("Reset the Founding Member counter back to 100 open spots? This clears the founding badge from all current members and cannot be undone. Do this only before launch.")) return;
    try {
      const res = await axios.post(`${API}/admin/founding/reset`, {}, { headers });
      setFounding(res.data);
      toast.success("Founding counter reset — 100 spots open.");
    } catch {
      toast.error("Could not reset. Try again.");
    }
  };

  const patchLead = async (id, patch) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    try {
      await axios.patch(`${API}/admin/leads/${id}`, patch, { headers });
    } catch {
      setLeads(prev);
      toast.error("Could not save. Try again.");
    }
  };

  const sources = useMemo(() => [...new Set(leads.map((l) => l.source).filter(Boolean))], [leads]);

  const filtered = useMemo(() => {
    let out = leads.filter((l) => {
      if (tierFilter !== "all" && l.tier !== tierFilter) return false;
      if (sourceFilter !== "all" && l.source !== sourceFilter) return false;
      if (hotOnly && !(l.hot || l.playbook_complete)) return false;
      if (q.trim()) {
        const t = q.toLowerCase();
        if (!(`${l.name} ${l.email} ${l.phone}`.toLowerCase().includes(t))) return false;
      }
      return true;
    });
    out = [...out].sort((a, b) => {
      if (sort === "score_high") return (b.score || 0) - (a.score || 0);
      if (sort === "score_low") return (a.score || 0) - (b.score || 0);
      const da = new Date(a.created_at).getTime(), dbb = new Date(b.created_at).getTime();
      return sort === "oldest" ? da - dbb : dbb - da;
    });
    return out;
  }, [leads, q, tierFilter, sourceFilter, hotOnly, sort]);

  const exportCsv = () => {
    const cols = ["name", "email", "phone", "equipment", "source", "score", "tier", "status", "booked_call", "hot", "playbook_complete", "admin_notes", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(",")].concat(
      filtered.map((l) => cols.map((c) => esc(c === "source" ? sourceLabel(l[c]) : l[c])).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `routehaul-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} leads to CSV.`);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <motion.form onSubmit={login} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm bg-white border border-slate-200 rounded-md p-8">
          <div className="flex items-center gap-2 font-heading font-black text-lg text-slate-900 mb-1">
            <Truck className="w-5 h-5 text-brand" /> ROUTEHAUL
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">Admin access</h1>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="admin-passcode" type={showPass ? "text" : "password"} autoCapitalize="none" autoCorrect="off" autoComplete="off" spellCheck={false} value={passcode} onChange={(e) => setPasscode(e.target.value)} placeholder="Enter passcode" className="h-12 pl-10 pr-11" />
            <button type="button" data-testid="admin-passcode-toggle" onClick={() => setShowPass((v) => !v)} aria-label={showPass ? "Hide passcode" : "Show passcode"} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors">
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <Button data-testid="admin-login" type="submit" disabled={loading} className="w-full h-12 mt-4 rounded-full bg-brand hover:bg-brand-hover text-white font-bold">
            {loading ? "Checking…" : "Enter dashboard"}
          </Button>
          <a href="/" className="block text-center text-sm text-slate-400 hover:text-slate-900 mt-4">← Back to site</a>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="backdrop-blur-xl bg-white/80 border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-black text-slate-900">
            <Truck className="w-5 h-5 text-brand" /> ROUTEHAUL <span className="text-slate-300 font-normal">/ Leads</span>
          </div>
          <a href="/" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Site
          </a>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 md:p-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {KPIS.map((k) => (
            <div key={k.key} data-testid={`kpi-${k.key}`} className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400">{k.label}</span>
                <k.icon className="w-4 h-4 text-slate-300" />
              </div>
              <div className="font-mono font-bold text-4xl tabular text-slate-900">{stats?.[k.key] ?? 0}</div>
              {k.key === "qualified" && <div className="text-xs text-slate-400 mt-1">{stats?.qualified_rate ?? 0}% of leads</div>}
            </div>
          ))}
        </div>

        {/* Founding Member offer */}
        {founding && (
          <div className="bg-white border border-slate-200 rounded-md p-6 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="admin-founding">
            <div>
              <div className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-bold text-amber-600 mb-1">
                <Crown className="w-4 h-4" /> Founding Member offer
              </div>
              <div className="font-mono font-bold text-2xl tabular text-slate-900" data-testid="admin-founding-count">
                {founding.claimed} / {founding.limit} claimed
              </div>
              <div className="text-sm font-semibold text-slate-500 mt-0.5">{founding.remaining} spots left</div>
              <div className="text-sm mt-1">
                {founding.active
                  ? <span className="text-emerald-600 font-semibold">Offer is live ({founding.intro_price}/mo for the {founding.intro_window}).</span>
                  : <span className="text-slate-500 font-semibold">Offer closed — all 100 spots claimed.</span>}
              </div>
            </div>
            <Button onClick={resetFounding} variant="outline" data-testid="reset-founding" className="rounded-full border-slate-300 font-bold">
              Reset counter to 100
            </Button>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3 justify-between">
            <h2 className="font-heading text-lg font-semibold text-slate-900">Captured leads <span className="text-slate-400 font-normal text-sm">({filtered.length})</span></h2>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="lead-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / phone" className="h-10 pl-9 w-56" />
              </div>
              <select data-testid="filter-tier" value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm text-slate-700 bg-white">
                <option value="all">All tiers</option>
                <option value="qualified">Qualified</option>
                <option value="developing">Developing</option>
              </select>
              <select data-testid="filter-source" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm text-slate-700 bg-white">
                <option value="all">All sources</option>
                {sources.map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}
              </select>
              <button data-testid="filter-hot" onClick={() => setHotOnly((v) => !v)}
                className={`h-10 px-3 rounded-md text-sm font-bold inline-flex items-center gap-1.5 border transition-colors ${hotOnly ? "bg-orange-100 text-brand border-orange-200" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}>
                <Flame className="w-4 h-4" /> Hot only
              </button>
              <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm text-slate-700 bg-white">
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="score_high">Score: high → low</option>
                <option value="score_low">Score: low → high</option>
              </select>
              <Button data-testid="export-leads-csv" onClick={exportCsv} className="h-10 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold">
                <Download className="w-4 h-4 mr-2" /> CSV
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Flag</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-12">No leads match your filters.</TableCell></TableRow>
                )}
                {filtered.map((l) => (
                  <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="align-top">
                    <TableCell className="font-medium text-slate-900 py-3">
                      {l.name}
                      {l.equipment && <div className="text-xs text-slate-400 font-normal mt-0.5">{l.equipment}</div>}
                    </TableCell>
                    <TableCell className="text-slate-600 py-3">
                      <a href={`mailto:${l.email}`} className="hover:text-brand block">{l.email}</a>
                      <a href={`tel:${l.phone}`} className="font-mono text-sm text-slate-500 hover:text-brand">{l.phone}</a>
                    </TableCell>
                    <TableCell className="py-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600">{sourceLabel(l.source)}</span>
                    </TableCell>
                    <TableCell className="text-right font-mono tabular font-semibold py-3">{l.score}</TableCell>
                    <TableCell className="py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${l.tier === "qualified" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{l.tier}</span>
                    </TableCell>
                    <TableCell className="py-3">
                      <select data-testid={`lead-status-${l.id}`} value={l.status || "new"} onChange={(e) => patchLead(l.id, { status: e.target.value })}
                        className={`h-8 rounded-full px-2.5 text-xs font-bold border-0 cursor-pointer ${STATUS_STYLES[l.status || "new"]}`}>
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="won">Won</option>
                        <option value="lost">Lost</option>
                      </select>
                    </TableCell>
                    <TableCell className="py-3">
                      {(l.hot || l.playbook_complete) ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-brand" data-testid={`hot-badge-${l.id}`}>
                          <Flame className="w-3 h-3" /> Hot
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="py-3 min-w-[180px]">
                      <input data-testid={`lead-notes-${l.id}`} defaultValue={l.admin_notes || ""}
                        onBlur={(e) => { if (e.target.value !== (l.admin_notes || "")) patchLead(l.id, { admin_notes: e.target.value }); }}
                        placeholder="Add a note…" className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
                    </TableCell>
                    <TableCell className="text-right text-slate-500 text-sm py-3 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden mt-8" data-testid="partner-clicks-table">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
            <Flame className="w-4 h-4 text-brand" />
            <h2 className="font-heading text-lg font-semibold text-slate-900">Partner link clicks</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Partner</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Clicks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clicks.length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-10">No partner clicks yet.</TableCell></TableRow>
              )}
              {clicks.map((c) => (
                <TableRow key={c.partner_id} className="h-12">
                  <TableCell className="font-medium text-slate-900">{c.name}</TableCell>
                  <TableCell className="text-slate-500 uppercase text-xs tracking-[0.1em] font-bold">{c.category}</TableCell>
                  <TableCell className="text-right font-mono tabular font-semibold">{c.clicks}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
